import React, { useEffect, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import StatusPill from '../components/StatusPill';
import TeamReadinessPanel from '../components/TeamReadinessPanel';

const statusLabels = {
  not_started: 'No iniciado',
  in_training: 'En formación',
  ready_to_practice: 'Listo para practicar',
  ready_with_supervision: 'Listo para aplicar con supervisión',
};

const repositoryStatusLabels = {
  pending: 'Pendiente de creación',
  creating: 'Creación iniciada',
  provisioning: 'Preparando plantilla',
  not_provisioned: 'Sin repositorio',
  active: 'Repositorio activo',
  conflict: 'Conflicto de identidad',
  unavailable: 'Acceso no disponible',
  deletion_pending: 'Eliminación en curso',
  deleting: 'Eliminación en curso',
  deletion_failed: 'Eliminación pendiente',
  deleted: 'Repositorio eliminado',
  personal_historical: 'Repositorio personal histórico',
};

const organizationDiagnosticLabels = {
  missing_administration: 'falta el permiso Administration (Read and write) en la instalación',
  missing_contents: 'falta el permiso Contents (Read and write) en la instalación',
  app_missing_members_permission: 'la GitHub App no tiene el permiso de organización Members -- agrégalo y acepta el upgrade en la instalación',
  not_a_member: 'no eres miembro de esta organización',
  membership_not_active: 'tu membresía en esta organización aún no está activa',
};

const deletionStatusLabels = {
  deleting: 'Eliminación en curso',
  succeeded: 'Eliminación completada',
  failed: 'No se pudo eliminar',
  intervention_required: 'Requiere intervención',
};

function errorDetail(detail) {
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || String(item)).join(' · ');
  }
  if (detail && typeof detail === 'object') return detail.message;
  return detail;
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'include', ...options,
    headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(errorDetail(data.detail) || 'No se pudo completar la operación.');
  }
  return data;
}

function repositoryStatusLabel(status) {
  if (!status) return 'Estado no disponible';
  return repositoryStatusLabels[status]
    || status.replaceAll('_', ' ').replace(/^./, (character) => character.toUpperCase());
}

function repositoryStatusColor(status) {
  if (status === 'deleted') return 'green';
  if (status === 'conflict' || status === 'unavailable') return 'red';
  if (status === 'pending' || status === 'creating' || status === 'provisioning' || status === 'deleting' || status === 'deletion_pending' || status === 'deletion_failed') return 'amber';
  return 'blue';
}

function deletionStatusColor(status) {
  if (status === 'succeeded') return 'green';
  if (status === 'failed' || status === 'intervention_required') return 'red';
  return 'amber';
}

function WorkshopCard({ apiUrl, workshop, onReload }) {
  const [organizations, setOrganizations] = useState([]);
  const [organizationDiagnostics, setOrganizationDiagnostics] = useState([]);
  const [organizationLogin, setOrganizationLogin] = useState(workshop?.organization_login || '');
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [organizationsError, setOrganizationsError] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [message, setMessage] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const deleteConfirmationRef = useRef(null);
  const repositoryStatus = workshop?.repository_status;
  const repositoryDeleted = repositoryStatus === 'deleted';
  const repositoryConflict = repositoryStatus === 'conflict' || workshop?.status === 'conflict';
  const repositoryDeleting = repositoryStatus === 'deleting'
    || repositoryStatus === 'deletion_pending'
    || workshop?.deletion?.status === 'deleting';
  const repositoryPending = repositoryStatus === 'pending' || repositoryStatus === 'not_provisioned';
  const repositoryProvisioning = repositoryPending
    || repositoryStatus === 'creating'
    || repositoryStatus === 'provisioning';
  const needsOrganization = !workshop
    || (repositoryProvisioning && !workshop.organization_login && !repositoryConflict && !repositoryDeleting);
  const canRetryProvisioning = Boolean(workshop)
    && repositoryProvisioning
    && !repositoryConflict
    && !repositoryDeleting
    && !repositoryDeleted;
  const organizationLocked = Boolean(workshop?.organization_login);
  const startDisabled = busyAction !== null
    || organizationsLoading
    || !organizationLogin
    || (!organizationLocked && !organizations.length);
  const deleteBlockers = workshop?.delete_blockers || [];

  useEffect(() => {
    if (!needsOrganization) return undefined;
    let cancelled = false;
    setOrganizationsLoading(true);
    setOrganizationsError(null);
    setOrganizations([]);
    setOrganizationDiagnostics([]);
    requestJson(`${apiUrl}/api/readiness/workshop/organizations`)
      .then((data) => {
        if (cancelled) return;
        const eligible = Array.isArray(data?.organizations) ? data.organizations : [];
        const diagnostics = Array.isArray(data?.diagnostics) ? data.diagnostics : [];
        setOrganizations(eligible);
        setOrganizationDiagnostics(diagnostics);
        setOrganizationLogin((current) => {
          const preferred = current || workshop?.organization_login || '';
          return eligible.some((organization) => organization.login === preferred) ? preferred : '';
        });
      })
      .catch((error) => {
        if (!cancelled) setOrganizationsError(error.message);
      })
      .finally(() => {
        if (!cancelled) setOrganizationsLoading(false);
      });
    return () => { cancelled = true; };
  }, [apiUrl, needsOrganization, workshop?.organization_login]);

  useEffect(() => {
    if (!repositoryDeleted) return;
    setDeleteOpen(false);
    setDeleteConfirmation('');
  }, [repositoryDeleted]);

  useEffect(() => {
    if (deleteOpen) deleteConfirmationRef.current?.focus();
  }, [deleteOpen]);

  const start = async () => {
    if (!organizationLogin) {
      setMessage({ type: 'error', text: 'Selecciona una organización autorizada para iniciar el taller.' });
      return;
    }
    setBusyAction('start'); setMessage(null);
    try {
      await requestJson(`${apiUrl}/api/readiness/workshop/start`, {
        method: 'POST',
        body: JSON.stringify({ organization_login: organizationLogin }),
      });
      await onReload();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      await onReload();
    }
    finally { setBusyAction(null); }
  };

  const validate = async () => {
    setBusyAction('validate'); setMessage(null);
    try {
      await requestJson(`${apiUrl}/api/readiness/workshop/validate`, { method: 'POST' });
      await onReload();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      await onReload();
    }
    finally { setBusyAction(null); }
  };

  const deleteRepository = async (event) => {
    event.preventDefault();
    if (deleteConfirmation !== workshop?.repo_full_name) return;
    setBusyAction('delete'); setMessage(null);
    try {
      await requestJson(`${apiUrl}/api/readiness/workshop/delete`, {
        method: 'POST',
        body: JSON.stringify({ confirmation: deleteConfirmation }),
      });
      setDeleteOpen(false);
      setDeleteConfirmation('');
      await onReload();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      await onReload();
    }
    finally { setBusyAction(null); }
  };

  const refreshDeletion = async () => {
    setBusyAction('delete-status'); setMessage(null);
    try {
      await requestJson(`${apiUrl}/api/readiness/workshop/delete`, {
        method: 'POST',
        body: JSON.stringify({ confirmation: workshop?.repo_full_name }),
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      await onReload();
      setBusyAction(null);
    }
  };

  const organizationPicker = (
    <div className="workshop-organization-picker">
      <label htmlFor="workshop-organization">Organización autorizada</label>
      <select
        id="workshop-organization"
        value={organizationLogin}
        disabled={organizationLocked || organizationsLoading || busyAction !== null}
        onChange={(event) => { setOrganizationLogin(event.target.value); setMessage(null); }}
      >
        <option value="">Selecciona una organización…</option>
        {organizations.map((organization) => (
          <option key={organization.installation_id} value={organization.login}>{organization.login}</option>
        ))}
      </select>
      {organizationsLoading && <small>Consultando instalaciones de GitHub autorizadas…</small>}
      {organizationsError && <div className="admin-message error" role="alert">{organizationsError}</div>}
      {!organizationsLoading && !organizationsError && organizations.length === 0 && (
        organizationDiagnostics.length > 0 ? (
          <div className="admin-message error" role="alert">
            No hay organizaciones elegibles todavía:
            <ul>
              {organizationDiagnostics.map((diagnostic) => (
                <li key={diagnostic.login}>
                  <strong>{diagnostic.login}</strong>: {organizationDiagnosticLabels[diagnostic.reason] || diagnostic.reason}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="admin-message error" role="alert">No hay organizaciones elegibles. Confirma que eres miembro activo y que la GitHub App de FORGE tiene Administration y Contents en Read and write; después reconecta GitHub.</div>
        )
      )}
      {organizationLogin && (
        <div className="workshop-organization-selected">
          {organizations.find((organization) => organization.login === organizationLogin)?.avatar_url && (
            <img src={organizations.find((organization) => organization.login === organizationLogin).avatar_url} alt="" />
          )}
          <span>El repositorio privado se creará en <strong>{organizationLogin}</strong>.</span>
        </div>
      )}
    </div>
  );

  return (
    <article className="forge-panel readiness-activity-card">
      <div className="forge-section-eyebrow">Actividad práctica 01</div>
      <h2>Taller guiado FORGE</h2>
      <p>Crea un repositorio privado dentro de una organización autorizada, desarrolla un clasificador de solicitudes sintéticas y avanza con evidencia por cada etapa.</p>
      {!workshop ? (
        <>
          {organizationPicker}
          <button className="forge-button primary" disabled={startDisabled} onClick={start}>{busyAction === 'start' ? 'Creando…' : 'Iniciar taller'}</button>
        </>
      ) : (
        <>
          <div className="workshop-status">
            <div className="workshop-status-pills">
              <StatusPill color={workshop.status === 'completed' ? 'green' : workshop.status === 'conflict' ? 'red' : 'blue'}>{workshop.status}</StatusPill>
              <StatusPill color={repositoryStatusColor(repositoryStatus)}>{repositoryStatusLabel(repositoryStatus)}</StatusPill>
              {workshop.deletion?.status && <StatusPill color={deletionStatusColor(workshop.deletion.status)}>{deletionStatusLabels[workshop.deletion.status] || workshop.deletion.status}</StatusPill>}
            </div>
            <strong>{workshop.completion_pct}%</strong>
          </div>
          <dl className="workshop-repository-meta">
            {workshop.organization_login && <div><dt>Organización</dt><dd>{workshop.organization_login}</dd></div>}
            {workshop.repo_full_name && <div><dt>Repositorio</dt><dd>{workshop.repo_full_name}</dd></div>}
            {workshop.curriculum_version != null && <div><dt>Ruta asociada</dt><dd>Versión {workshop.curriculum_version}</dd></div>}
          </dl>
          {workshop.last_error && <div className="admin-message error" role="alert">{workshop.last_error}</div>}
          {workshop.deletion?.last_error && <div className="admin-message error" role="alert">{workshop.deletion.last_error}</div>}
          {repositoryConflict && !workshop.last_error && <div className="admin-message error" role="alert">La identidad del repositorio está en conflicto. Solicita intervención antes de continuar.</div>}
          {needsOrganization && organizationPicker}
          {!repositoryDeleted && workshop.repo_url && <a href={workshop.repo_url} target="_blank" rel="noreferrer">Abrir repositorio privado ↗</a>}
          <ol className="workshop-stage-list">{(workshop.stages || []).map((stage) => <li key={stage.id} className={stage.complete ? 'complete' : ''}><span>{stage.complete ? '✓' : '○'}</span><div><strong>{stage.title}</strong><small>{stage.objective}</small><p>{stage.instructions}</p><small>Evidencia: {stage.evidence_path || 'pull request'}</small><em>{stage.feedback}</em></div></li>)}</ol>
          {repositoryDeleted ? (
            <div className="workshop-history-state">
              <strong>Repositorio eliminado</strong>
              <p>Se conserva el avance, la versión curricular y la evidencia permitida del taller. El repositorio y su contenido ya no están disponibles desde FORGE.</p>
              {workshop.deletion?.completed_at && <small>Eliminado el {new Date(workshop.deletion.completed_at).toLocaleString('es-MX')}</small>}
            </div>
          ) : (
            <>
              {canRetryProvisioning ? (
                <button className="forge-button primary" disabled={startDisabled} onClick={start}>{busyAction === 'start' ? 'Preparando…' : 'Reintentar preparación'}</button>
              ) : (
                !repositoryDeleting && repositoryStatus === 'active' && workshop.repo_url && <button className="forge-button primary" disabled={busyAction !== null} onClick={validate}>{busyAction === 'validate' ? 'Validando…' : 'Validar evidencia'}</button>
              )}
              {workshop.repo_full_name && !workshop.can_delete && deleteBlockers.length > 0 && (
                <div className="workshop-delete-blockers">
                  <strong>El repositorio todavía no puede eliminarse</strong>
                  <ul>{deleteBlockers.map((blocker, index) => <li key={`${blocker}-${index}`}>{blocker}</li>)}</ul>
                </div>
              )}
              {workshop.can_delete && !repositoryDeleting && !deleteOpen && (
                <button className="forge-button danger workshop-delete-trigger" disabled={busyAction !== null} onClick={() => { setDeleteOpen(true); setMessage(null); }}>Eliminar repositorio del taller</button>
              )}
              {repositoryDeleting && (
                <button className="forge-button workshop-delete-trigger" disabled={busyAction !== null} onClick={refreshDeletion}>{busyAction === 'delete-status' ? 'Consultando…' : 'Revisar estado de eliminación'}</button>
              )}
              {workshop.can_delete && !repositoryDeleting && deleteOpen && (
                <form className="workshop-delete-confirmation" onSubmit={deleteRepository} aria-describedby="workshop-delete-warning">
                  <strong>Eliminación permanente</strong>
                  <p id="workshop-delete-warning">Esta acción es irreversible. Se eliminarán permanentemente archivos, ramas, issues, pull requests y todos los demás artefactos del repositorio.</p>
                  <label htmlFor="workshop-delete-confirmation">Escribe <code>{workshop.repo_full_name}</code> para confirmar.</label>
                  <input
                    id="workshop-delete-confirmation"
                    ref={deleteConfirmationRef}
                    value={deleteConfirmation}
                    autoComplete="off"
                    spellCheck="false"
                    disabled={busyAction !== null}
                    onChange={(event) => setDeleteConfirmation(event.target.value)}
                  />
                  <div>
                    <button type="button" className="forge-button" disabled={busyAction !== null} onClick={() => { setDeleteOpen(false); setDeleteConfirmation(''); }}>Cancelar</button>
                    <button type="submit" className="forge-button danger" disabled={busyAction !== null || deleteConfirmation !== workshop.repo_full_name}>{busyAction === 'delete' ? 'Eliminando…' : 'Eliminar definitivamente'}</button>
                  </div>
                </form>
              )}
            </>
          )}
        </>
      )}
      {message && <div className={`admin-message ${message.type}`} role={message.type === 'error' ? 'alert' : 'status'} aria-live="polite">{message.text}</div>}
    </article>
  );
}

export function simulationStartState({ workshopComplete, attemptsRemaining, busy }) {
  if (!workshopComplete) {
    return {
      disabled: true,
      explanation: 'Completa y valida primero el taller práctico para iniciar el simulacro.',
    };
  }
  return {
    disabled: Boolean(busy) || attemptsRemaining === 0,
    explanation: null,
  };
}

export function SimulationCard({ apiUrl, simulation, workshopComplete, onReload }) {
  const [attempt, setAttempt] = useState(simulation.current_attempt);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const startState = simulationStartState({
    workshopComplete,
    attemptsRemaining: simulation.attempts_remaining,
    busy,
  });
  const start = async () => {
    setBusy(true); setMessage(null); setResult(null); setAnswers({});
    try { setAttempt(await requestJson(`${apiUrl}/api/readiness/simulation/start`, { method: 'POST' })); }
    catch (error) { setMessage({ type: 'error', text: error.message }); }
    finally { setBusy(false); }
  };
  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setMessage(null);
    try {
      const data = await requestJson(`${apiUrl}/api/readiness/simulation/${attempt.id}/submit`, { method: 'POST', body: JSON.stringify({ answers }) });
      setResult(data.result); setAttempt(null); await onReload();
    } catch (error) { setMessage({ type: 'error', text: error.message }); }
    finally { setBusy(false); }
  };
  return (
    <article className="forge-panel readiness-activity-card simulation-card">
      <div className="forge-section-eyebrow">Actividad práctica 02</div>
      <h2>Simulacro final</h2>
      <p>Decide qué aprobar, rechazar o escalar ante specs incompletas, prompt injection y agentes fuera de autoridad.</p>
      <div className="simulation-meta"><StatusPill color={simulation.passed ? 'green' : 'amber'}>{simulation.passed ? 'Aprobado' : `${simulation.attempts_remaining} intentos disponibles`}</StatusPill>{simulation.retry_at && <small>Próximo intento: {new Date(simulation.retry_at).toLocaleString('es-MX')}</small>}</div>
      {!attempt && <button className="forge-button primary" disabled={startState.disabled} onClick={start}>{busy ? 'Preparando…' : result && !result.passed ? 'Intentar de nuevo' : simulation.passed ? 'Practicar de nuevo' : 'Iniciar simulacro'}</button>}
      {!attempt && startState.explanation && <div className="admin-message info" role="status">{startState.explanation}</div>}
      {attempt?.catalog_available === false && <div className="admin-message error">Esta versión del simulacro ya no está disponible. Solicita apoyo para iniciar una versión vigente.</div>}
      {attempt && attempt.catalog_available !== false && <form className="simulation-form" onSubmit={submit}>{attempt.scenarios.map((scenario) => <fieldset key={scenario.id}><legend>{scenario.prompt}</legend>{scenario.options.map((option) => <label key={option.id}><input type="radio" name={scenario.id} value={option.id} checked={answers[scenario.id] === option.id} onChange={() => setAnswers((prev) => ({ ...prev, [scenario.id]: option.id }))} required />{option.text}</label>)}</fieldset>)}<button className="forge-button primary" disabled={busy} type="submit">Enviar decisiones</button></form>}
      {result && <div className={`simulation-result ${result.passed ? 'success' : 'retry'}`}><strong>{result.score}% · {result.passed ? 'Aprobado' : 'Revisa y vuelve a intentarlo'}</strong>{result.feedback.map((item) => <p key={item.scenario_id}>{item.correct ? '✓' : '○'} {item.explanation}</p>)}</div>}
      {message && <div className={`admin-message ${message.type}`}>{message.text}</div>}
    </article>
  );
}

export default function Readiness() {
  const { apiUrl } = useOutletContext();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = async () => { try { setData(await requestJson(`${apiUrl}/api/readiness`)); setError(null); } catch (err) { setError(err.message); } };
  useEffect(() => { load(); }, [apiUrl]);
  const markRead = async (recipientId) => { await requestJson(`${apiUrl}/api/readiness/notifications/${recipientId}/read`, { method: 'POST' }); await load(); };
  if (!data) return <div className="forge-page"><div className="forge-panel">{error || 'Cargando preparación…'}</div></div>;
  const readiness = data.readiness;
  return (
    <div className="forge-page readiness-page">
      <div className="forge-page-header"><div><div className="forge-section-eyebrow">Aprender · Preparación</div><h1 className="forge-title">Preparación para aplicar FORGE</h1><p className="forge-lead">Formación, práctica y criterio humano sin rankings ni evaluación laboral.</p></div><StatusPill color={readiness.readiness_status === 'ready_with_supervision' ? 'green' : 'blue'}>{statusLabels[readiness.readiness_status]}</StatusPill></div>
      <section className="readiness-self-grid">
        <div className="forge-panel"><span>Ruta común</span><strong>{readiness.completion_pct}%</strong><Link to="/learn">Continuar aprendiendo</Link></div>
        <div className="forge-panel"><span>Certificación vigente</span><strong>{readiness.certified_current ? 'Sí' : 'Pendiente'}</strong></div>
        <div className="forge-panel"><span>Taller práctico</span><strong>{readiness.workshop_complete ? 'Completo' : 'Pendiente'}</strong></div>
        <div className="forge-panel"><span>Simulacro</span><strong>{readiness.simulation_passed ? 'Aprobado' : 'Pendiente'}</strong></div>
      </section>
      <section className="forge-panel readiness-notifications"><div className="learning-section-head"><div><div className="forge-section-eyebrow">Acompañamiento</div><h2>Notificaciones</h2></div><span>{data.notifications.filter((item) => !item.read).length} sin leer</span></div>{data.notifications.map((item) => <article key={item.recipient_id} className={item.read ? 'read' : ''}><div><strong>{item.title}</strong><small>{item.group} · @{item.sender}</small><p>{item.body}</p>{item.link_path && <Link to={item.link_path}>Abrir contexto</Link>}</div>{!item.read && <button className="forge-button" onClick={() => markRead(item.recipient_id)}>Marcar leída</button>}</article>)}{!data.notifications.length && <div className="forge-empty">No tienes notificaciones.</div>}</section>
      <TeamReadinessPanel apiUrl={apiUrl} groups={data.responsible_groups} templates={data.notification_templates} onReload={load} />
      <section className="readiness-activities"><WorkshopCard apiUrl={apiUrl} workshop={data.workshop} onReload={load} /><SimulationCard apiUrl={apiUrl} simulation={data.simulation} workshopComplete={readiness.workshop_complete} onReload={load} /></section>
    </div>
  );
}
